import { useEffect, useRef, useState } from "react";
import { testimonials } from "@/data/testimonials";
import { useLocale } from "@/i18n/useLocale";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import "./TestimonialsSection.css";

/** Cada cuanto avanza solo, en ms. Con solo dos testimonios no conviene mas
 *  rapido: hay que dar tiempo a leer antes de que cambie. */
const AUTOPLAY_MS = 7000;

/**
 * Testimonios de clientes reales, en un carrusel que avanza solo.
 *
 * Va oscura, entre novedades y contacto: es la ultima prueba de confianza
 * antes de pedir el contacto, el lugar donde mas rinde.
 *
 * El avance automatico se pausa con el mouse encima o el foco adentro (para
 * no interrumpir la lectura ni robarle el foco al teclado), y no arranca si
 * el usuario pidio menos movimiento -las flechas y los puntos siguen andando
 * a mano en ese caso.
 */
function TestimonialsSection() {
  const { t, locale } = useLocale();
  const headerRef = useScrollReveal<HTMLDivElement>();
  const cardRef = useScrollReveal<HTMLDivElement>({ delay: 100 });
  const [index, setIndex] = useState(0);
  const paused = useRef(false);
  const [isManual, setIsManual] = useState(false);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const id = window.setInterval(() => {
      if (!paused.current) {
        setIsManual(false);
        setIndex((i) => (i + 1) % testimonials.length);
      }
    }, AUTOPLAY_MS);

    return () => window.clearInterval(id);
  }, []);

  const goPrev = () => {
    setIsManual(true);
    setIndex((i) => (i - 1 + testimonials.length) % testimonials.length);
  };
  const goNext = () => {
    setIsManual(true);
    setIndex((i) => (i + 1) % testimonials.length);
  };

  const current = testimonials[index];

  return (
    <section
      className="testimonials-section"
      aria-labelledby="testimonials-titulo"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      onFocus={() => (paused.current = true)}
      onBlur={() => (paused.current = false)}
    >
      <div className="testimonials-section__content">
        <div className="testimonials-section__header" ref={headerRef}>
          <p className="testimonials-section__eyebrow">{t.testimonials.eyebrow}</p>
          <h2 className="testimonials-section__title" id="testimonials-titulo">
            {t.testimonials.title}
          </h2>
        </div>

        {/* La tarjeta ocupa todo el ancho; los controles van debajo, no a los
            costados -flanquearla con flechas le restaba ancho justo en las
            pantallas donde mas hace falta (mobile). */}
        <div ref={cardRef}>
          {/* key=index: remonta la tarjeta en cada cambio para que la entrada
              se anime de nuevo (si no, React reutiliza el nodo y no hay nada
              que animar). */}
          <div
            className="testimonials-section__card"
            key={index}
            aria-live={isManual ? "polite" : "off"}
          >
            <span className="testimonials-section__quote-mark" aria-hidden="true">
              &ldquo;
            </span>
            <p className="testimonials-section__quote">
              {locale === "en" ? current.quoteEn : current.quote}
            </p>
            <footer className="testimonials-section__author">
              <span className="testimonials-section__name">{current.name}</span>
              <span className="testimonials-section__role">
                {locale === "en" ? current.roleEn : current.role}
              </span>
            </footer>
          </div>

          <div className="testimonials-section__controls">
            <button
              type="button"
              className="testimonials-section__arrow"
              onClick={goPrev}
              aria-label={t.testimonials.prev}
            >
              ←
            </button>

            <div
              className="testimonials-section__dots"
              role="tablist"
              aria-label={t.testimonials.eyebrow}
            >
              {testimonials.map((tm, i) => (
                <button
                  key={tm.name}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={tm.name}
                  className={`testimonials-section__dot${i === index ? " is-active" : ""}`}
                  onClick={() => {
                    setIsManual(true);
                    setIndex(i);
                  }}
                />
              ))}
            </div>

            <button
              type="button"
              className="testimonials-section__arrow"
              onClick={goNext}
              aria-label={t.testimonials.next}
            >
              →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TestimonialsSection;
